import type { ComicSyncState } from '@tanko/shared'
import { isBackedUp } from './comic-sync'

/**
 * 作品的狀態燈。
 *
 * 書架卡片與列表列都只留一顆圓點的位置，因此「這本現在是什麼狀況」必須先收斂成一種。
 * 判斷集中在這裡，兩種呈現才不會各自挑一套規則；圖示留在元件，這個模組保持純邏輯。
 */

export type ComicStatusKind = 'backed-up' | 'not-backed-up' | 'indexing' | 'index-failed' | 'missing'

/** 燈的語氣：決定顏色與亮不亮，不決定文字。 */
export type ComicStatusTone = 'lit' | 'unlit' | 'busy' | 'warning' | 'danger'

export type ComicStatus = {
  kind: ComicStatusKind
  tone: ComicStatusTone
  /** 游標停留時顯示的說明，同時是輔助技術讀到的可及名稱——圓點本身沒有文字。 */
  label: string
}

export type ComicStatusInput = {
  /** 這台裝置有沒有這一本的檔案。沒有的話那幾個本機旗標講的都是別台裝置的事。 */
  hasLocalCopy: boolean
  syncState: ComicSyncState
  /** 本地來源裡解析不到這本作品。 */
  missing?: boolean
  /** 上一次索引失敗，尚未重試成功。 */
  indexFailed?: boolean
  /** 索引正在進行。 */
  indexing?: boolean
}

const descriptors: Record<ComicStatusKind, Omit<ComicStatus, 'kind'>> = {
  'backed-up': { tone: 'lit', label: '已備份到私人雲端' },
  'not-backed-up': { tone: 'unlit', label: '尚未備份，雲端還沒有這一本的內容' },
  indexing: { tone: 'busy', label: '正在建立索引' },
  'index-failed': { tone: 'warning', label: '索引失敗，等待重試' },
  // 與封面替代文字用同一句，兩個地方講的話才一致。
  missing: { tone: 'danger', label: '找不到檔案' },
}

const status = (kind: ComicStatusKind): ComicStatus => ({ kind, ...descriptors[kind] })

/**
 * 挑出要點的那一顆燈。
 *
 * 本機檔案的問題排在已備份之前：一本已經備份、但本機檔案不見了的作品，該講的是檔案
 * 不見了——雲端有沒有副本不是當下要處理的事。
 *
 * 尚未備份一律點暗燈。書庫只有一份清單之後，這個狀態回答的是「這一本在別台裝置上讀
 * 不讀得到」，而那是需要知道的事，不是雜訊——先前在本地分頁不點燈的理由（滿頁都是
 * 未備份，標了等於沒標）建立在「另一頁才是雲端」之上，單一清單裡不成立。
 *
 * 沒有本機副本時不看 missing／indexFailed／indexing：那些旗標描述的是本機檔案，而
 * 清單涵蓋所有裝置上傳的作品，套用另一台裝置的檔案狀態只會標錯。
 */
export function comicStatus(input: ComicStatusInput): ComicStatus {
  if (input.hasLocalCopy) {
    if (input.missing) return status('missing')
    if (input.indexFailed) return status('index-failed')
    if (input.indexing) return status('indexing')
  }
  return status(isBackedUp(input) ? 'backed-up' : 'not-backed-up')
}
